import { useState } from "react";
import {
  AllergyIntolerance,
  CodeableConcept,
  Condition,
  DiagnosticReport,
  Encounter,
  HumanName,
  Immunization,
  MedicationAdministration,
  MedicationRequest,
  Observation,
  Procedure,
  Resource,
  ServiceRequest,
} from "@medplum/fhirtypes";
import { Smart } from "@metriport/fhir-sdk";
import { IconButton } from "@chakra-ui/react-v3";
import { FiFileText, FiX } from "react-icons/fi";
import { NoteDisplay } from "../note-display";
import { SimpleModal } from "../simple-modal";
import { Z_INDEX } from "../../constants/z-index";

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function codeableConceptText(cc: CodeableConcept | undefined): string | undefined {
  if (!cc) return undefined;
  return cc.text ?? cc.coding?.[0]?.display ?? cc.coding?.[0]?.code;
}

function humanNameText(name: HumanName | undefined): string {
  if (!name) return "Unknown";
  if (name.text) return name.text;
  const parts: string[] = [];
  if (name.prefix) parts.push(...name.prefix);
  if (name.given) parts.push(...name.given);
  if (name.family) parts.push(name.family);
  if (name.suffix) parts.push(...name.suffix);
  return parts.join(" ") || "Unknown";
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function hasCategory(obs: Observation, code: string): boolean {
  return (
    obs.category?.some(function checkCoding(cat) {
      return cat.coding?.some(function matchCode(c) {
        return c.code === code;
      });
    }) ?? false
  );
}

function observationValue(obs: Observation): string {
  if (obs.valueQuantity) {
    const unit = obs.valueQuantity.unit ?? "";
    return `${obs.valueQuantity.value ?? ""} ${unit}`.trim();
  }
  if (obs.valueString) return obs.valueString;
  if (obs.valueCodeableConcept) {
    return codeableConceptText(obs.valueCodeableConcept) ?? "";
  }
  if (typeof obs.valueBoolean === "boolean") return obs.valueBoolean ? "Yes" : "No";
  if (typeof obs.valueInteger === "number") return String(obs.valueInteger);
  return "";
}

function observationReferenceRange(obs: Observation): string {
  const rr = obs.referenceRange?.[0];
  if (!rr) return "";
  if (rr.text) return rr.text;
  const low = rr.low ? `${rr.low.value ?? ""}${rr.low.unit ? ` ${rr.low.unit}` : ""}` : "";
  const high = rr.high ? `${rr.high.value ?? ""}${rr.high.unit ? ` ${rr.high.unit}` : ""}` : "";
  if (low && high) return `${low} - ${high}`;
  if (low) return `>= ${low}`;
  if (high) return `<= ${high}`;
  return "";
}

function interpretationColor(text: string): string {
  const lower = text.toLowerCase();
  if (lower.includes("high") || lower.includes("critical")) return "red";
  if (lower.includes("low")) return "orange";
  if (lower.includes("normal")) return "green";
  return "blue";
}

function medicationName(r: MedicationRequest | MedicationAdministration): string {
  const cc = r.medicationCodeableConcept;
  return codeableConceptText(cc) ?? r.resourceType;
}

function deduplicateById<T extends Resource>(resources: T[]): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const r of resources) {
    const key = r.id ?? "";
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    result.push(r);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Data gathering
// ---------------------------------------------------------------------------

type LabPanel = {
  panelName: string;
  observations: Observation[];
};

type EncounterData = {
  practitioners: string[];
  locations: string[];
  organization: string | undefined;
  conditions: Condition[];
  vitalObservations: Observation[];
  labPanels: LabPanel[];
  ungroupedLabs: Observation[];
  socialHistoryObs: Observation[];
  otherObservations: Observation[];
  medicationRequests: MedicationRequest[];
  medicationAdministrations: MedicationAdministration[];
  procedures: Procedure[];
  noteReports: DiagnosticReport[];
  immunizations: Immunization[];
  allergies: AllergyIntolerance[];
  serviceRequests: ServiceRequest[];
};

function gatherEncounterData(encounter: Smart<Encounter>): EncounterData {
  const participants = encounter.getParticipants();
  const practitioners = participants
    .filter(function isPractitioner(p) {
      return p.resourceType === "Practitioner";
    })
    .map(function getName(p) {
      return humanNameText((p as { name?: HumanName[] }).name?.[0]);
    });

  const practitionerRoleNames = participants
    .filter(function isPractitionerRole(p) {
      return p.resourceType === "PractitionerRole";
    })
    .map(function getRoleName(p) {
      const role = p as { code?: CodeableConcept[] };
      return codeableConceptText(role.code?.[0]) ?? "PractitionerRole";
    });

  const allPractitionerNames = [...practitioners, ...practitionerRoleNames].filter(Boolean);

  const locationResources = encounter.getLocations();
  const locationNames = locationResources
    .map(function getLocName(l) {
      return (l as { name?: string }).name;
    })
    .filter(function hasName(n): n is string {
      return !!n;
    });

  const org = encounter.getServiceProvider();
  const orgName = org ? (org as { name?: string }).name : undefined;

  const diagnosisConditions = encounter.getDiagnosisConditions();
  const forwardConditions = diagnosisConditions.filter(function isCondition(r) {
    return r.resourceType === "Condition";
  }) as unknown as Condition[];

  const allReferencing = encounter.getReferencingResources();

  const reverseConditions: Condition[] = [];
  const vitalObservations: Observation[] = [];
  const directLabObservations: Observation[] = [];
  const socialHistoryObs: Observation[] = [];
  const otherObservations: Observation[] = [];
  const medicationRequests: MedicationRequest[] = [];
  const medicationAdministrations: MedicationAdministration[] = [];
  const procedures: Procedure[] = [];
  const allDiagnosticReports: Smart<DiagnosticReport>[] = [];
  const immunizations: Immunization[] = [];
  const allergies: AllergyIntolerance[] = [];
  const serviceRequests: ServiceRequest[] = [];

  for (const r of allReferencing) {
    switch (r.resourceType) {
      case "Condition":
        reverseConditions.push(r);
        break;
      case "Observation":
        if (hasCategory(r, "vital-signs")) vitalObservations.push(r);
        else if (hasCategory(r, "laboratory")) directLabObservations.push(r);
        else if (hasCategory(r, "social-history")) socialHistoryObs.push(r);
        else otherObservations.push(r);
        break;
      case "MedicationRequest":
        medicationRequests.push(r);
        break;
      case "MedicationAdministration":
        medicationAdministrations.push(r);
        break;
      case "Procedure":
        procedures.push(r);
        break;
      case "DiagnosticReport":
        allDiagnosticReports.push(r as Smart<DiagnosticReport>);
        break;
      case "Immunization":
        immunizations.push(r);
        break;
      case "AllergyIntolerance":
        allergies.push(r);
        break;
      case "ServiceRequest":
        serviceRequests.push(r);
        break;
    }
  }

  const conditions = deduplicateById([...forwardConditions, ...reverseConditions]);

  // 2-hop walk: DiagnosticReport -> result[] -> Observations
  // Split reports into lab panels (have result[]) vs clinical notes (have presentedForm)
  const labPanels: LabPanel[] = [];
  const noteReports: DiagnosticReport[] = [];
  const panelObservationIds = new Set<string>();

  for (const report of allDiagnosticReports) {
    const hasResults = report.result && report.result.length > 0;
    const hasPresentedForm = report.presentedForm && report.presentedForm.length > 0;

    if (hasResults) {
      const panelObs = (report as Smart<DiagnosticReport>).getResults();
      const labObs = panelObs.filter(function isLab(o) {
        return hasCategory(o, "laboratory") || panelObs.length > 0;
      });
      if (labObs.length > 0) {
        labPanels.push({
          panelName: codeableConceptText(report.code) ?? "Lab Panel",
          observations: labObs,
        });
        for (const o of labObs) {
          if (o.id) panelObservationIds.add(o.id);
        }
      }
    }

    if (hasPresentedForm) {
      noteReports.push(report);
    } else if (!hasResults) {
      noteReports.push(report);
    }
  }

  // Ungrouped labs: direct reverse-ref labs not already in a panel
  const ungroupedLabs = directLabObservations.filter(function notInPanel(obs) {
    return !obs.id || !panelObservationIds.has(obs.id);
  });

  return {
    practitioners: allPractitionerNames,
    locations: locationNames,
    organization: orgName,
    conditions,
    vitalObservations,
    labPanels,
    ungroupedLabs,
    socialHistoryObs,
    otherObservations,
    medicationRequests,
    medicationAdministrations,
    procedures,
    noteReports,
    immunizations,
    allergies,
    serviceRequests,
  };
}

// ---------------------------------------------------------------------------
// Styles: dense, borderless, 90s data-terminal aesthetic
// ---------------------------------------------------------------------------

const s = {
  table: {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    fontSize: 12,
    lineHeight: "16px",
  } as const,
  th: {
    textAlign: "left",
    padding: "2px 6px",
    fontWeight: 600,
    color: "#888",
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
    overflow: "hidden",
    borderBottom: "1px solid #eee",
    borderRight: "1px solid #f0f0f0",
  } as const,
  td: {
    padding: "2px 6px",
    verticalAlign: "top",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 0,
    cursor: "default",
    borderBottom: "1px solid #f0f0f0",
    borderRight: "1px solid #f0f0f0",
  } as const,
  muted: { color: "#888" } as const,
  bold: { fontWeight: 600 } as const,
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    paddingTop: 10,
    margin: "0 0 6px 0",
  } as const,
  panelTitle: { fontSize: 12, fontWeight: 600, margin: "0 0 2px 0" } as const,
  count: { fontWeight: 400, color: "#999" } as const,
  tag: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 600,
    padding: "0 4px",
    borderRadius: 2,
    lineHeight: "16px",
  } as const,
  viewBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 11,
    color: "#555",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "1px 4px",
    borderRadius: 2,
  } as const,
  metaRow: { display: "flex", gap: 6, fontSize: 12, lineHeight: "18px" } as const,
  metaLabel: { color: "#888", fontWeight: 500, flexShrink: 0 } as const,
  hr: { border: "none", borderTop: "1px solid #e5e5e5", margin: "10px 0" } as const,
} as const;

const tagColors: Record<string, { bg: string; color: string }> = {
  red: { bg: "#fee2e2", color: "#b91c1c" },
  orange: { bg: "#ffedd5", color: "#c2410c" },
  green: { bg: "#dcfce7", color: "#15803d" },
  blue: { bg: "#dbeafe", color: "#1d4ed8" },
  gray: { bg: "#f3f4f6", color: "#6b7280" },
};

const defaultTagColor = { bg: "#f3f4f6", color: "#6b7280" };

function Tag({ color, children }: { color: string; children: React.ReactNode }) {
  const scheme = tagColors[color] ?? defaultTagColor;
  return (
    <span style={{ ...s.tag, backgroundColor: scheme.bg, color: scheme.color }}>{children}</span>
  );
}

function textContent(node: React.ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textContent).join("");
  if (node && typeof node === "object" && "props" in node) {
    return textContent((node as { props: { children?: React.ReactNode } }).props.children);
  }
  return "";
}

function Td({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) {
  const text = textContent(children);
  return (
    <td style={{ ...s.td, ...style }} title={text}>
      {children}
    </td>
  );
}

// ---------------------------------------------------------------------------
// Shared layout primitives
// ---------------------------------------------------------------------------

function MetadataRow({ label, value }: { label: string; value: string | undefined }) {
  if (!value) return null;
  return (
    <div style={s.metaRow}>
      <span style={s.metaLabel}>{label}:</span>
      <span>{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Encounter Context (header)
// ---------------------------------------------------------------------------

function EncounterContextSection({
  encounter,
  data,
}: {
  encounter: Smart<Encounter>;
  data: EncounterData;
}) {
  const reasons = encounter.reasonCode
    ?.map(function getReasonText(rc) {
      return codeableConceptText(rc);
    })
    .filter(Boolean)
    .join(", ");

  return (
    <div>
      {reasons && <MetadataRow label="Reason for Visit" value={reasons} />}
      {data.practitioners.length > 0 && (
        <MetadataRow label="Practitioner" value={data.practitioners.join(", ")} />
      )}
      {data.locations.length > 0 && (
        <MetadataRow label="Location" value={data.locations.join(", ")} />
      )}
      <MetadataRow label="Organization" value={data.organization} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Subjective
// ---------------------------------------------------------------------------

function AllergiesTable({ allergies }: { allergies: AllergyIntolerance[] }) {
  if (allergies.length === 0) return null;
  return (
    <div>
      <p style={s.sectionTitle}>
        Allergies <span style={s.count}>({allergies.length})</span>
      </p>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Allergen</th>
            <th style={s.th}>Criticality</th>
            <th style={s.th}>Category</th>
          </tr>
        </thead>
        <tbody>
          {allergies.map(function renderAllergy(a, i) {
            const critColor =
              a.criticality === "high"
                ? "red"
                : a.criticality === "unable-to-assess"
                ? "orange"
                : "green";
            return (
              <tr key={a.id ?? i}>
                <Td>{codeableConceptText(a.code) ?? "Unknown"}</Td>
                <Td>{a.criticality && <Tag color={critColor}>{a.criticality}</Tag>}</Td>
                <Td style={s.muted}>{a.category?.join(", ") ?? ""}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SocialHistoryTable({ observations }: { observations: Observation[] }) {
  if (observations.length === 0) return null;
  return (
    <div>
      <p style={s.sectionTitle}>
        Social History <span style={s.count}>({observations.length})</span>
      </p>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Observation</th>
            <th style={s.th}>Value</th>
          </tr>
        </thead>
        <tbody>
          {observations.map(function renderSocial(obs, i) {
            return (
              <tr key={obs.id ?? i}>
                <Td>{codeableConceptText(obs.code) ?? "Observation"}</Td>
                <Td>{observationValue(obs)}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Objective
// ---------------------------------------------------------------------------

function VitalsTable({ observations }: { observations: Observation[] }) {
  if (observations.length === 0) return null;
  return (
    <div>
      <p style={s.sectionTitle}>
        Vitals <span style={s.count}>({observations.length})</span>
      </p>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Vital Sign</th>
            <th style={s.th}>Value</th>
            <th style={s.th}>Date</th>
          </tr>
        </thead>
        <tbody>
          {observations.map(function renderVital(obs, i) {
            return (
              <tr key={obs.id ?? i}>
                <Td>{codeableConceptText(obs.code) ?? "Vital"}</Td>
                <Td style={s.bold}>{observationValue(obs)}</Td>
                <Td style={s.muted}>
                  {formatDate(obs.effectiveDateTime ?? obs.effectivePeriod?.start)}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LabObservationRows({ observations }: { observations: Observation[] }) {
  return (
    <>
      {observations.map(function renderLab(obs, i) {
        const interpText =
          obs.interpretation?.[0]?.text ?? obs.interpretation?.[0]?.coding?.[0]?.display;
        const interpColor = interpText ? interpretationColor(interpText) : undefined;
        return (
          <tr key={obs.id ?? i}>
            <Td>{codeableConceptText(obs.code) ?? "Lab"}</Td>
            <Td style={s.bold}>{observationValue(obs)}</Td>
            <Td>
              {interpText && interpColor ? <Tag color={interpColor}>{interpText}</Tag> : null}
            </Td>
            <Td style={s.muted}>{observationReferenceRange(obs)}</Td>
          </tr>
        );
      })}
    </>
  );
}

function LabsSection({
  panels,
  ungroupedLabs,
}: {
  panels: LabPanel[];
  ungroupedLabs: Observation[];
}) {
  const totalCount = panels.reduce(function countObs(sum, p) {
    return sum + p.observations.length;
  }, ungroupedLabs.length);
  if (totalCount === 0) return null;

  const labHeader = (
    <thead>
      <tr>
        <th style={s.th}>Test</th>
        <th style={s.th}>Value</th>
        <th style={s.th}>Interp.</th>
        <th style={s.th}>Ref. Range</th>
      </tr>
    </thead>
  );

  return (
    <div>
      <p style={s.sectionTitle}>
        Labs <span style={s.count}>({totalCount})</span>
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {panels.map(function renderPanel(panel, pi) {
          return (
            <div key={pi}>
              <p style={s.panelTitle}>
                {panel.panelName} <span style={s.count}>({panel.observations.length})</span>
              </p>
              <table style={s.table}>
                {labHeader}
                <tbody>
                  <LabObservationRows observations={panel.observations} />
                </tbody>
              </table>
            </div>
          );
        })}
        {ungroupedLabs.length > 0 && (
          <div>
            {panels.length > 0 && (
              <p style={s.panelTitle}>
                Other Labs <span style={s.count}>({ungroupedLabs.length})</span>
              </p>
            )}
            <table style={s.table}>
              {labHeader}
              <tbody>
                <LabObservationRows observations={ungroupedLabs} />
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function OtherObservationsTable({ observations }: { observations: Observation[] }) {
  if (observations.length === 0) return null;
  return (
    <div>
      <p style={s.sectionTitle}>
        Other Observations <span style={s.count}>({observations.length})</span>
      </p>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Observation</th>
            <th style={s.th}>Value</th>
            <th style={s.th}>Date</th>
          </tr>
        </thead>
        <tbody>
          {observations.map(function renderOther(obs, i) {
            return (
              <tr key={obs.id ?? i}>
                <Td>{codeableConceptText(obs.code) ?? "Observation"}</Td>
                <Td>{observationValue(obs)}</Td>
                <Td style={s.muted}>
                  {formatDate(obs.effectiveDateTime ?? obs.effectivePeriod?.start)}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ImmunizationsTable({ immunizations }: { immunizations: Immunization[] }) {
  if (immunizations.length === 0) return null;
  return (
    <div>
      <p style={s.sectionTitle}>
        Immunizations <span style={s.count}>({immunizations.length})</span>
      </p>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Vaccine</th>
            <th style={s.th}>Date</th>
            <th style={s.th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {immunizations.map(function renderImm(imm, i) {
            return (
              <tr key={imm.id ?? i}>
                <Td>{codeableConceptText(imm.vaccineCode) ?? "Immunization"}</Td>
                <Td style={s.muted}>{formatDate(imm.occurrenceDateTime)}</Td>
                <Td>{imm.status && <Tag color="gray">{imm.status}</Tag>}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Assessment
// ---------------------------------------------------------------------------

function DiagnosesTable({ conditions }: { conditions: Condition[] }) {
  if (conditions.length === 0) return null;
  return (
    <div>
      <p style={s.sectionTitle}>
        Diagnoses <span style={s.count}>({conditions.length})</span>
      </p>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Condition</th>
            <th style={s.th}>Status</th>
            <th style={s.th}>Onset</th>
          </tr>
        </thead>
        <tbody>
          {conditions.map(function renderCondition(c, i) {
            const status =
              c.clinicalStatus?.coding?.[0]?.display ?? c.clinicalStatus?.coding?.[0]?.code;
            const statusColor =
              status === "active" ? "green" : status === "resolved" ? "gray" : "blue";
            return (
              <tr key={c.id ?? i}>
                <Td>{codeableConceptText(c.code) ?? "Condition"}</Td>
                <Td>{status && <Tag color={statusColor}>{status}</Tag>}</Td>
                <Td style={s.muted}>{formatDate(c.onsetDateTime ?? c.recordedDate)}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ClinicalNotesTable({
  reports,
  onViewNote,
}: {
  reports: DiagnosticReport[];
  onViewNote: (report: DiagnosticReport) => void;
}) {
  if (reports.length === 0) return null;
  return (
    <div>
      <p style={s.sectionTitle}>
        Clinical Notes <span style={s.count}>({reports.length})</span>
      </p>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Note Type</th>
            <th style={s.th}>Category</th>
            <th style={s.th}>Date</th>
            <th style={s.th} />
          </tr>
        </thead>
        <tbody>
          {reports.map(function renderReport(r, i) {
            const hasContent = r.presentedForm && r.presentedForm.length > 0;
            return (
              <tr key={r.id ?? i}>
                <Td>{codeableConceptText(r.code) ?? "Report"}</Td>
                <Td style={s.muted}>
                  {r.category
                    ?.map(function getCat(cat) {
                      return codeableConceptText(cat);
                    })
                    .filter(Boolean)
                    .join(", ") ?? ""}
                </Td>
                <Td style={s.muted}>
                  {formatDate(r.effectiveDateTime ?? r.effectivePeriod?.start)}
                </Td>
                <Td>
                  {hasContent && (
                    <button
                      type="button"
                      style={s.viewBtn}
                      onClick={function openNote() {
                        onViewNote(r);
                      }}
                    >
                      <FiFileText size={12} /> View
                    </button>
                  )}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Plan
// ---------------------------------------------------------------------------

function MedicationsTable({
  requests,
  administrations,
}: {
  requests: MedicationRequest[];
  administrations: MedicationAdministration[];
}) {
  const allMeds = [
    ...requests.map(function toMedRow(r) {
      return {
        id: r.id,
        name: medicationName(r),
        dosage: r.dosageInstruction?.[0]?.text ?? "",
        status: r.status,
      };
    }),
    ...administrations.map(function toAdminRow(r) {
      return { id: r.id, name: medicationName(r), dosage: r.dosage?.text ?? "", status: r.status };
    }),
  ];
  if (allMeds.length === 0) return null;
  return (
    <div>
      <p style={s.sectionTitle}>
        Medications <span style={s.count}>({allMeds.length})</span>
      </p>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Medication</th>
            <th style={s.th}>Dosage</th>
            <th style={s.th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {allMeds.map(function renderMed(m, i) {
            return (
              <tr key={m.id ?? i}>
                <Td>{m.name}</Td>
                <Td style={{ ...s.muted, fontSize: 11 }}>{m.dosage}</Td>
                <Td>{m.status && <Tag color="gray">{m.status}</Tag>}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ProceduresTable({ procedures }: { procedures: Procedure[] }) {
  if (procedures.length === 0) return null;
  return (
    <div>
      <p style={s.sectionTitle}>
        Procedures <span style={s.count}>({procedures.length})</span>
      </p>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Procedure</th>
            <th style={s.th}>Status</th>
            <th style={s.th}>Date</th>
          </tr>
        </thead>
        <tbody>
          {procedures.map(function renderProc(p, i) {
            return (
              <tr key={p.id ?? i}>
                <Td>{codeableConceptText(p.code) ?? "Procedure"}</Td>
                <Td>{p.status && <Tag color="gray">{p.status}</Tag>}</Td>
                <Td style={s.muted}>
                  {formatDate(p.performedDateTime ?? p.performedPeriod?.start)}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OrdersTable({ serviceRequests }: { serviceRequests: ServiceRequest[] }) {
  if (serviceRequests.length === 0) return null;
  return (
    <div>
      <p style={s.sectionTitle}>
        Orders <span style={s.count}>({serviceRequests.length})</span>
      </p>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Order</th>
            <th style={s.th}>Status</th>
            <th style={s.th}>Priority</th>
          </tr>
        </thead>
        <tbody>
          {serviceRequests.map(function renderOrder(sr, i) {
            const priorityColor =
              sr.priority === "stat"
                ? "red"
                : sr.priority === "asap"
                ? "orange"
                : sr.priority === "urgent"
                ? "orange"
                : undefined;
            return (
              <tr key={sr.id ?? i}>
                <Td>{codeableConceptText(sr.code) ?? "Order"}</Td>
                <Td>{sr.status && <Tag color="gray">{sr.status}</Tag>}</Td>
                <Td>
                  {sr.priority && sr.priority !== "routine" && priorityColor && (
                    <Tag color={priorityColor}>{sr.priority}</Tag>
                  )}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Note viewer modal (still uses SimpleModal for overlay behavior)
// ---------------------------------------------------------------------------

function NoteViewerModal({
  report,
  onClose,
}: {
  report: DiagnosticReport | undefined;
  onClose: () => void;
}) {
  return (
    <SimpleModal
      isOpen={!!report}
      onClose={onClose}
      maxWidth="900px"
      backdropZIndex={Z_INDEX.PDF_MODAL_BACKDROP}
      contentZIndex={Z_INDEX.PDF_MODAL_CONTENT}
    >
      <SimpleModal.Header>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "#888" }}>
            {report ? codeableConceptText(report.code) ?? "" : ""}
          </span>
          <IconButton size="sm" variant="ghost" onClick={onClose} aria-label="Close">
            <FiX />
          </IconButton>
        </div>
      </SimpleModal.Header>
      <SimpleModal.Body>{report && <NoteDisplay resource={report} />}</SimpleModal.Body>
    </SimpleModal>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export type EncounterNarrativeViewProps = {
  encounter: Smart<Encounter>;
};

export function EncounterNarrativeView({ encounter }: EncounterNarrativeViewProps) {
  const [selectedNote, setSelectedNote] = useState<DiagnosticReport | undefined>(undefined);
  const data = gatherEncounterData(encounter);

  const labCount = data.labPanels.reduce(function count(sum, p) {
    return sum + p.observations.length;
  }, data.ungroupedLabs.length);
  const hasSections =
    data.conditions.length > 0 ||
    data.vitalObservations.length > 0 ||
    labCount > 0 ||
    data.socialHistoryObs.length > 0 ||
    data.otherObservations.length > 0 ||
    data.medicationRequests.length > 0 ||
    data.medicationAdministrations.length > 0 ||
    data.procedures.length > 0 ||
    data.noteReports.length > 0 ||
    data.immunizations.length > 0 ||
    data.allergies.length > 0 ||
    data.serviceRequests.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <EncounterContextSection encounter={encounter} data={data} />

      {!hasSections && (
        <p style={{ fontSize: 12, color: "#888" }}>No clinical data found for this encounter.</p>
      )}

      <DiagnosesTable conditions={data.conditions} />
      <AllergiesTable allergies={data.allergies} />
      <SocialHistoryTable observations={data.socialHistoryObs} />
      <VitalsTable observations={data.vitalObservations} />
      <LabsSection panels={data.labPanels} ungroupedLabs={data.ungroupedLabs} />
      <OtherObservationsTable observations={data.otherObservations} />
      <ImmunizationsTable immunizations={data.immunizations} />
      <MedicationsTable
        requests={data.medicationRequests}
        administrations={data.medicationAdministrations}
      />
      <ProceduresTable procedures={data.procedures} />
      <OrdersTable serviceRequests={data.serviceRequests} />
      <ClinicalNotesTable
        reports={data.noteReports}
        onViewNote={function handleViewNote(report) {
          setSelectedNote(report);
        }}
      />

      <NoteViewerModal
        report={selectedNote}
        onClose={function handleClose() {
          setSelectedNote(undefined);
        }}
      />
    </div>
  );
}
